/**
 * Single normalized error contract for the data layer (FIX_BACKLOG #9,
 * zod-response-validation.md PR-1).
 *
 * Before this module, `request()` rejected with THREE distinct shapes:
 *   1. `new Error('Network error')`            — no HTTP response at all
 *   2. `axiosError.response.data` (plain obj)  — the raw API error envelope
 *   3. `SchemaParseError`                       — Zod mismatch in typedRequest
 *
 * Interceptors, `QueryCache.onError`, and ~178 mutation `onError` callbacks
 * therefore had to defensively probe `error?.message` / `error?.errorsList`
 * on values that might not even be Error instances. `ApiError` collapses all
 * three into one class while PRESERVING the legacy property reads:
 * envelope fields (`success`, `data`, `message`, `errorsList`, `httpStatus`)
 * are copied onto the instance, so `res?.errorsList` destructuring in
 * existing `onError` handlers keeps working unchanged.
 */

/** Envelope error row shape used by backend validation errors. */
export interface ApiErrorField {
  field: string
  message: string
  [key: string]: unknown
}

export type ApiErrorKind = 'network' | 'http' | 'schema' | 'cancelled'

export class ApiError extends Error {
  override name = 'ApiError'
  /** What failed: transport (`network`), backend (`http`), Zod (`schema`), or a caller-initiated abort (`cancelled`). */
  readonly kind: ApiErrorKind
  /** HTTP status code, when a response was received. */
  readonly status?: number
  /** Legacy envelope fields — kept as own props for `res?.errorsList` consumers. */
  readonly success?: boolean
  readonly data?: unknown
  readonly errorsList?: ApiErrorField[]
  readonly httpStatus?: string
  /** The original rejected value, untouched, for triage logging. */
  readonly raw?: unknown

  constructor(
    message: string,
    options: {
      kind: ApiErrorKind
      status?: number
      success?: boolean
      data?: unknown
      errorsList?: ApiErrorField[]
      httpStatus?: string
      raw?: unknown
    },
  ) {
    super(message)
    this.kind = options.kind
    this.status = options.status
    this.success = options.success
    this.data = options.data
    this.errorsList = options.errorsList
    this.httpStatus = options.httpStatus
    this.raw = options.raw
  }
}

/**
 * Normalize anything a fetch can reject with into an `ApiError`.
 * Idempotent: an existing `ApiError` (incl. `SchemaParseError`) passes through.
 */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error

  const axiosError = error as {
    response?: { status?: number; data?: unknown }
    message?: string
    code?: string
    name?: string
  }

  // AbortSignal cancellation (signal forwarded by the TQ wrappers). Axios 1.x
  // rejects with CanceledError (code 'ERR_CANCELED'); a raw AbortSignal throws
  // DOMException 'AbortError'. Cancellation is not a failure — QueryCache.onError
  // filters kind 'cancelled' so unmount aborts never toast.
  if (axiosError?.code === 'ERR_CANCELED' || axiosError?.name === 'AbortError') {
    return new ApiError(axiosError?.message ?? 'Request cancelled', {
      kind: 'cancelled',
      raw: error,
    })
  }

  if (!axiosError?.response) {
    return new ApiError(axiosError?.message ?? 'Network error', {
      kind: 'network',
      raw: error,
    })
  }

  const envelope = (axiosError.response.data ?? {}) as {
    success?: boolean
    data?: unknown
    message?: string
    errorsList?: ApiErrorField[]
    httpStatus?: string
  }

  return new ApiError(envelope.message ?? axiosError.message ?? 'Request failed', {
    kind: 'http',
    status: axiosError.response.status,
    success: envelope.success,
    data: envelope.data,
    errorsList: envelope.errorsList,
    httpStatus: envelope.httpStatus,
    raw: axiosError.response.data,
  })
}
